import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAttendance } from '../../hooks/useAttendance';

/**
 * Global Clock Button Component
 * Highly aesthetic, session-aware, and synchronized via Redux.
 *
 * Shift-aware: an employee can have up to two rostered shifts a day. This
 * reads the same per-slot state (`slots`/`activeSlot`/`nextSlot`/
 * `allShiftsDone`) ESS-Mobile's clock-in screen already uses, so clocking in
 * for a second shift is visibly labeled "Shift 2" instead of looking
 * identical to the first, and the button disables once both are done instead
 * of silently re-using shift 1 forever.
 */
const ClockButton = () => {
    const { t } = useTranslation();
    const { isClockedIn, activeSlot, nextSlot, allShiftsDone, essClockEnabled, handleToggle, isLoading } = useAttendance();

    const activeShiftLabel = (activeSlot?.slot === 2 && activeSlot?.rostered !== false) ? t('clockButton.shift2') : activeSlot?.shiftName || null;
    const nextShiftLabel = (nextSlot?.slot === 2 && nextSlot?.rostered !== false) ? t('clockButton.shift2') : null;

    // The company has turned off clocking from ESS: punches only come from the
    // biometric device, so there is no button - just a note saying why.
    if (!essClockEnabled) {
        return (
            <div
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-gray-50 text-gray-500 border-2 border-gray-100"
                title={t('clockButton.biometricPunchOnlyTooltip') as string}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                <span>{t('clockButton.biometricPunchOnly')}</span>
            </div>
        );
    }

    if (!isLoading && allShiftsDone && !isClockedIn) {
        return (
            <div
                className="relative flex items-center gap-2 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-600 border-2 border-emerald-100"
                title={t('clockButton.shiftsCompletedTooltip') as string}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('clockButton.shiftsCompleted')}</span>
            </div>
        );
    }

    return (
        <button
            onClick={handleToggle}
            disabled={isLoading}
            className={`
                relative flex items-center gap-3 px-5 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-500
                ${isClockedIn
                    ? 'bg-rose-50 text-rose-600 border-2 border-rose-100 hover:bg-rose-100 shadow-lg shadow-rose-100/50'
                    : 'bg-indigo-600 text-white border-2 border-indigo-500 hover:bg-indigo-700 shadow-xl shadow-indigo-200/50'
                }
                ${isLoading ? 'opacity-50 cursor-not-allowed scale-95' : 'hover:-translate-y-0.5 active:scale-95'}
            `}
        >
            {isLoading ? (
                <div className="flex items-center gap-2">
                    <svg className="animate-spin h-3 w-3 text-current" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('clockButton.syncing')}</span>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3" />
                            <circle cx="12" cy="12" r="9" strokeWidth={2} />
                        </svg>
                        {!isClockedIn && (
                            <span className="absolute -top-1 -right-1 rtl:right-auto rtl:-left-1 flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                            </span>
                        )}
                    </div>
                    <span>
                        {isClockedIn ? t('clockButton.clockOut') : t('clockButton.clockIn')}
                        {isClockedIn && activeShiftLabel && ` — ${activeShiftLabel}`}
                        {!isClockedIn && nextShiftLabel && ` — ${nextShiftLabel}`}
                    </span>

                    {/* Timestamp Pill */}
                    {isClockedIn && (
                        <div className="ml-1 rtl:ml-0 rtl:mr-1 pl-3 rtl:pl-0 rtl:pr-3 border-l rtl:border-l-0 rtl:border-r border-rose-200">
                             <span className="text-[9px] font-bold opacity-80">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    )}
                </>
            )}
        </button>
    );
};

export default ClockButton;
